import moment from 'moment';
import { t } from 'i18next';

// Date format the API stores start/end dates in.
export const OUT_OF_OFFICE_DATE_FORMAT = 'YYYY-MM-DD';

// Placeholders the user can drop into subject and body. The backend replaces
// them with the real dates when the auto reply goes out — the editor resolves
// them locally so the user sees the final wording while typing.
export const OUT_OF_OFFICE_VARS = [
  { key: 'startDate', label: 'Start date' },
  { key: 'endDate', label: 'End date' },
];

const VAR_REGEX_SOURCE = `%%(${OUT_OF_OFFICE_VARS.map((v) => v.key).join('|')})%%`;

// The token as it is stored and sent to the API. Never shown to the user.
export function getVarToken(key) {
  return `%%${key}%%`;
}

// Formats an API date for display, returns null for missing/broken values so
// callers can fall back to the raw placeholder.
export function formatVarDate(date, dateFormat = 'DD.MM.YYYY') {
  if (!date) return null;
  const parsed = moment(date, OUT_OF_OFFICE_DATE_FORMAT);
  return parsed.isValid() ? parsed.format(dateFormat) : null;
}

// What a placeholder looks like before a date has been picked. `[Start date]`
// reads as a slot to fill; `%%startDate%%` would only leak the storage format
// to the user. The raw token stays in the value that is sent to the API.
export function getVarPlaceholderLabel(key) {
  const variable = OUT_OF_OFFICE_VARS.find((entry) => entry.key === key);
  const label = variable ? variable.label : key;
  // t() yields undefined until i18next has initialised — fall back to the
  // English label rather than rendering "[undefined]".
  return `[${t(label) || label}]`;
}

// Builds the { startDate, endDate } replacement map used by the preview.
export function buildVarValues(data = {}, dateFormat) {
  return {
    startDate: formatVarDate(data.startDate, dateFormat),
    endDate: formatVarDate(data.endDate, dateFormat),
  };
}

// Splits a text into plain segments and placeholder segments so the preview can
// highlight what each placeholder turns into. Placeholders without a value yet
// are returned unresolved instead of being dropped.
export function splitOutOfOfficeVars(text = '', values = {}) {
  const parts = [];
  const regex = new RegExp(VAR_REGEX_SOURCE, 'g');
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex)
      parts.push({ type: 'text', value: text.slice(lastIndex, match.index) });

    const value = values[match[1]];
    parts.push({
      type: 'var',
      key: match[1],
      value: value || getVarPlaceholderLabel(match[1]),
      resolved: Boolean(value),
    });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length)
    parts.push({ type: 'text', value: text.slice(lastIndex) });

  return parts;
}

export function resolveOutOfOfficeVars(text = '', values = {}) {
  return splitOutOfOfficeVars(text, values)
    .map((part) => part.value)
    .join('');
}

// How often a placeholder appears across subject and body — drives the "in use"
// badge on the insert chips.
export function countVarUsage(varKey, ...texts) {
  const regex = new RegExp(`%%${varKey}%%`, 'g');
  return texts.reduce(
    (sum, text) => sum + ((text || '').match(regex) || []).length,
    0,
  );
}

// active = running right now, scheduled = starts later, expired = already over.
export function getOutOfOfficeStatus(notification) {
  const today = moment().format(OUT_OF_OFFICE_DATE_FORMAT);
  const { startDate, endDate } = notification || {};

  if (startDate && startDate > today) return 'scheduled';
  if (endDate && endDate < today) return 'expired';
  return 'active';
}

const STATUS_ORDER = { active: 0, scheduled: 1, expired: 2 };

// Running notifications first, then upcoming ones by start date, expired ones
// last with the most recent on top.
export function sortNotifications(notifications = []) {
  return [...notifications].sort((a, b) => {
    const statusA = getOutOfOfficeStatus(a);
    const statusB = getOutOfOfficeStatus(b);

    if (STATUS_ORDER[statusA] !== STATUS_ORDER[statusB])
      return STATUS_ORDER[statusA] - STATUS_ORDER[statusB];

    if (statusA === 'expired')
      return (b.endDate || '').localeCompare(a.endDate || '');

    return (a.startDate || '').localeCompare(b.startDate || '');
  });
}

// The API reads back `emailCredentials` (expanded objects) but is written with
// `emailCredentialIds` — accept either, and either shape of entry.
export function extractCredentialIds(notification) {
  if (!notification) return [];

  const source = Array.isArray(notification.emailCredentialIds)
    ? notification.emailCredentialIds
    : notification.emailCredentials;

  if (!Array.isArray(source)) return [];
  return source
    .map((entry) => (entry && typeof entry === 'object' ? entry.id : entry))
    .filter((id) => id !== undefined && id !== null);
}

// check if vacation reply is active for a given credentialId. If the vacation reply is not enabled, return false. If the vacation reply is enabled, check if the credentialId is in the list of credentialIds for the vacation reply.
export function isVacationReplyActiveFor(vacationNotification, credentialId) {
  if (!vacationNotification?.isEnabled) return false;
  return extractCredentialIds(vacationNotification).includes(credentialId);
}

// returns true if the notification is a Abwesenheit aus Urlauben notification, false otherwise.
export function isVacationNotification(notification) {
  return notification?.type === 'vacation';
}

// The body is HTML since the switch to the rich text editor. Quill's empty
// state is `<p><br></p>`, so "no text" has to be judged on the rendered text —
// an embedded image (e.g. a signature logo) still counts as content.
export function isHtmlEmpty(html) {
  if (!html) return true;
  const container = document.createElement('div');
  container.innerHTML = html;
  const text = (container.textContent || '').replace(
    /[\u200B-\u200D\uFEFF]/g,
    '',
  );
  if (text.trim()) return false;
  return !container.querySelector('img');
}

// Turns the plain text templates (i18n strings with \n) into the HTML the
// editor and the API work with: blank lines separate paragraphs, single
// newlines become line breaks.
export function plainTextToHtml(text = '') {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br/>')}</p>`)
    .join('');
}

// --- HTML body chips ------------------------------------------------------
// The Quill body editor shows the tokens as atomic chips (OutOfOfficeVarBlot),
// mirroring the subject field: chips in the editor, raw `%%key%%` tokens in
// the stored value.

// Prepares HTML for the editor: known tokens in text nodes become chip spans
// the editor turns into embeds. Unknown %% sequences stay untouched.
export function replaceVarTokensWithChips(html, varValues = {}) {
  if (!html || !html.includes('%%')) return html || '';

  const container = document.createElement('div');
  container.innerHTML = html;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);

  textNodes.forEach((textNode) => {
    const parts = splitOutOfOfficeVars(textNode.textContent, varValues);
    if (!parts.some((part) => part.type === 'var')) return;

    const fragment = document.createDocumentFragment();
    parts.forEach((part) => {
      if (part.type === 'var') {
        const chip = document.createElement('span');
        chip.className = 'ooo-chip';
        chip.dataset.var = part.key;
        chip.textContent = part.value;
        fragment.appendChild(chip);
      } else {
        fragment.appendChild(document.createTextNode(part.value));
      }
    });
    textNode.replaceWith(fragment);
  });

  return container.innerHTML;
}

// Inverse for the way out: chip spans back to raw tokens, so the API never
// sees the cosmetic labels. Also drops Quill's FEFF embed guard characters —
// they are layout, not content.
export function replaceVarChipsWithTokens(html) {
  if (!html) return html || '';
  if (!html.includes('data-var')) return html.replace(/\uFEFF/g, '');

  const container = document.createElement('div');
  container.innerHTML = html;

  container.querySelectorAll('[data-var]').forEach((chip) => {
    chip.replaceWith(document.createTextNode(getVarToken(chip.dataset.var)));
  });

  return container.innerHTML.replace(/\uFEFF/g, '');
}

export function getDurationInDays(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const start = moment(startDate, OUT_OF_OFFICE_DATE_FORMAT);
  const end = moment(endDate, OUT_OF_OFFICE_DATE_FORMAT);
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return null;
  return end.diff(start, 'days') + 1;
}
